import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaArrowRight, FaBook, FaCheckCircle, 
  FaClock, FaNewspaper, FaLightbulb, FaQuoteLeft,
  FaPlay, FaPause, FaRedo, FaHome, FaShare, FaTwitter, FaLinkedin, FaTrophy
} from 'react-icons/fa';
import { ASSETS, GLOBE_TROTTER_CHARACTERS, getRandomQuote } from '../config/schools';

// Composant Badge partageable
function ShareableBadge({ courseName, completedEpochs, totalEpochs, onClose }) {
  const percentage = Math.round((completedEpochs / totalEpochs) * 100);
  
  const shareOnTwitter = () => {
    const text = `J'ai complété ${percentage}% du cours "${courseName}" sur @DailyGlobeSchool ! #Journalisme #Formation #HistoireDeLaPresse`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.5, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        className="bg-gradient-to-br from-amber-600 via-yellow-500 to-orange-500 p-1 rounded-2xl max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl p-6 text-center">
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-6xl mb-4"
          >
            <FaTrophy className="mx-auto text-yellow-500" />
          </motion.div>
          
          <h2 className="comic-title text-2xl text-gray-800 mb-2">
            Certificat de Réussite
          </h2>
          
          <div className="bg-gradient-to-r from-amber-700 to-yellow-600 text-white py-2 px-4 rounded-lg mb-4">
            <p className="text-lg font-bold">{courseName}</p>
          </div>
          
          <div className="flex justify-center items-center gap-2 mb-4">
            <div className="text-4xl font-bold text-green-500">{percentage}%</div>
            <div className="text-gray-500 text-sm text-left">
              <p>{completedEpochs}/{totalEpochs} époques</p>
              <p>complétées</p>
            </div>
          </div>
          
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`text-2xl ${i < Math.ceil(completedEpochs * 5 / totalEpochs) ? '' : 'grayscale opacity-30'}`}
              >
                ⭐
              </motion.span>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            DailyGlobe School - {new Date().toLocaleDateString('fr-FR')}
          </p>
          
          <div className="border-t pt-4">
            <p className="text-sm text-gray-600 mb-3">Partager mon badge :</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={shareOnTwitter}
                className="flex items-center gap-2 bg-blue-400 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FaTwitter /> Twitter
              </button>
              <button
                onClick={shareOnLinkedIn}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FaLinkedin /> LinkedIn
              </button>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="mt-4 text-gray-500 hover:text-gray-700 text-sm"
          >
            Fermer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Données du cours - 7 époques
const COURSE_DATA = {
  title: "Histoire de la Presse",
  professor: GLOBE_TROTTER_CHARACTERS.petitXXeme,
  epochs: [
    {
      id: 1,
      title: "Avant 1600 - Les Origines",
      subtitle: "De la voix aux premiers écrits",
      color: "#8B4513",
      icon: "📜",
      timeline: [
        { year: "Antiquité", event: "Acta Diurna romains affichés sur les murs" },
        { year: "1438", event: "Gutenberg invente la typographie mobile" },
        { year: "1539", event: "Édit de François Ier sur le monopole d'affichage" }
      ],
      lessons: [
        {
          title: "L'information avant l'imprimerie",
          points: [
            "Diffusion par voie orale, courrier ou affichage",
            "Les gouvernants utilisent l'info pour les lois et la défense",
            "Les Romains créent l'\"Acta Diurna\" (faits du jour)"
          ],
          keyFact: "Les Romains avaient déjà un système d'information publique !"
        },
        {
          title: "La révolution Gutenberg",
          points: [
            "Apparition du libelle, du placard, de l'almanach",
            "En France : l'Affaire des Placards secoue le royaume",
            "François Ier accorde le monopole d'affichage en 1539"
          ],
          keyFact: "L'imprimerie change tout, mais peu de gens savent lire !"
        },
        {
          title: "Les freins au développement",
          points: [
            "L'analphabétisme est généralisé",
            "Le crieur public supplée à l'écrit",
            "Le coût d'impression reste prohibitif"
          ],
          keyFact: "Le papier coûte une fortune à l'époque !"
        }
      ],
      quiz: {
        question: "Qui a inventé la typographie mobile ?",
        options: ["François Ier", "Johannes Gutenberg", "Louis XI", "Théophraste Renaudot"],
        correct: 1
      }
    },
    {
      id: 2,
      title: "1600-1700 - Naissance de la Presse",
      subtitle: "Les premiers journaux apparaissent",
      color: "#1B4965",
      icon: "📰",
      timeline: [
        { year: "1605", event: "Premier périodique : Relation (Strasbourg)" },
        { year: "1610", event: "Le Mercure François, premier annuel français" },
        { year: "1631", event: "La Gazette de Théophraste Renaudot" },
        { year: "1648", event: "Début des Mazarinades" }
      ],
      lessons: [
        {
          title: "Le premier journal au monde",
          points: [
            "\"Relation\" paraît à Strasbourg en décembre 1605",
            "C'est un hebdomadaire de 4 pages",
            "Fondé par Johann Carolus"
          ],
          keyFact: "Strasbourg, alors allemande, voit naître la presse !"
        },
        {
          title: "La Gazette de Renaudot",
          points: [
            "Théophraste Renaudot obtient un privilège royal en 1631",
            "Grâce au Cardinal de Richelieu",
            "Premier grand périodique français"
          ],
          keyFact: "Sans le soutien de Richelieu, pas de Gazette !"
        },
        {
          title: "Les Mazarinades",
          points: [
            "Pamphlets satiriques contre le Cardinal Mazarin",
            "Plus de 4000 écrits entre 1648 et 1652",
            "Ancêtres du journalisme d'opinion"
          ],
          keyFact: "Les fake news existent depuis le XVIIe siècle !"
        }
      ],
      quiz: {
        question: "Qui a fondé La Gazette en 1631 ?",
        options: ["Richelieu", "Johann Carolus", "Théophraste Renaudot", "Louis XIV"],
        correct: 2
      }
    },
    {
      id: 3,
      title: "Le XVIIe siècle",
      subtitle: "Censure et nouveaux périodiques",
      color: "#9B59B6",
      icon: "👑",
      timeline: [
        { year: "1672", event: "Le Mercure galant (presse people)" },
        { year: "1674", event: "Louis XIV durcit la censure" },
        { year: "1685", event: "Presse clandestine des huguenots" },
        { year: "1706", event: "Institutionnalisation du contrôle" }
      ],
      lessons: [
        {
          title: "Censure sous Louis XIV",
          points: [
            "1674 : durcissement du contrôle royal",
            "Autorisation obligatoire pour chaque ouvrage",
            "Le Parlement exerce la censure"
          ],
          keyFact: "Impossible de critiquer le Roi-Soleil !"
        },
        {
          title: "Le Mercure galant",
          points: [
            "Lancé par Donneau de Visé en 1672",
            "Ancêtre de la presse people",
            "350 pages mensuelles sur l'actualité mondaine"
          ],
          keyFact: "Les potins de Versailles passionnaient déjà !"
        },
        {
          title: "La presse clandestine",
          points: [
            "Après 1685, les huguenots créent des journaux secrets",
            "Publications \"sous le manteau\"",
            "Les intellectuels préfèrent les livres, plus discrets"
          ],
          keyFact: "La censure n'a jamais empêché l'information de circuler !"
        }
      ],
      quiz: {
        question: "Qu'est-ce que le Mercure galant ?",
        options: ["Un journal politique", "L'ancêtre de la presse people", "Un pamphlet", "Un journal anglais"],
        correct: 1
      }
    },
    {
      id: 4,
      title: "Le XVIIIe siècle - Les Lumières",
      subtitle: "Révolution et liberté",
      color: "#FFB703",
      icon: "💡",
      timeline: [
        { year: "1777", event: "Journal de Paris, 1er quotidien français" },
        { year: "1787", event: "1er amendement US (liberté de presse)" },
        { year: "1788", event: "Fondation du Times à Londres" },
        { year: "1789", event: "Déclaration des Droits de l'Homme" }
      ],
      lessons: [
        {
          title: "Le premier quotidien français",
          points: [
            "Le Journal de Paris paraît en 1777",
            "Fondé par Cadet de Vaux, Coranrez et Dussieux",
            "Traite de culture et de faits divers"
          ],
          keyFact: "Il faut attendre 1777 pour un quotidien français !"
        },
        {
          title: "La presse spécialisée explose",
          points: [
            "Journal économique (1751), Journal de Médecine (1754)",
            "Journal des Dames (1759), Courrier de la Mode (1768)",
            "Les Français veulent des sujets variés"
          ],
          keyFact: "La presse féminine existe depuis 1759 !"
        },
        {
          title: "La Révolution française",
          points: [
            "1789 : \"tout citoyen peut écrire librement\"",
            "Le contrôle de la presse est aboli",
            "Danton et Camille Desmoulins créent Le Cordelier"
          ],
          keyFact: "La liberté de la presse est un droit de l'Homme !"
        }
      ],
      quiz: {
        question: "Quel journal britannique est fondé en 1788 ?",
        options: ["The Guardian", "The Times", "Daily Mail", "The Weekly News"],
        correct: 1
      }
    },
    {
      id: 5,
      title: "Le XIXe siècle",
      subtitle: "Télégraphe, rotative et publicité",
      color: "#E63946",
      icon: "⚙️",
      timeline: [
        { year: "1800", event: "Napoléon rétablit la censure" },
        { year: "1836", event: "Invention de la publicité (Girardin)" },
        { year: "1845", event: "Invention de la rotative (Hoe)" },
        { year: "1851", event: "Agence Reuters fondée" },
        { year: "1881", event: "Loi sur la liberté de la presse" }
      ],
      lessons: [
        {
          title: "Napoléon contre la presse",
          points: [
            "17 janvier 1800 : retour de la censure",
            "60 journaux supprimés d'un coup",
            "1811 : seulement 4 quotidiens autorisés"
          ],
          keyFact: "Napoléon réduisit la presse à 4 journaux !"
        },
        {
          title: "Les révolutions technologiques",
          points: [
            "1836 : télégraphe électrique Londres-Birmingham",
            "Émile de Girardin invente la publicité",
            "La rotative permet d'imprimer des milliers d'exemplaires"
          ],
          keyFact: "La pub finance les journaux depuis 1836 !"
        },
        {
          title: "La loi de 1881",
          points: [
            "29 juillet 1881 : loi sur la liberté de la presse",
            "\"L'imprimerie et la librairie sont libres\"",
            "Cette loi est toujours en vigueur aujourd'hui !"
          ],
          keyFact: "La loi de 1881 protège encore la presse française !"
        }
      ],
      quiz: {
        question: "Qui a inventé la publicité pour financer la presse ?",
        options: ["Napoléon", "Richard Hoe", "Émile de Girardin", "Paul Reuter"],
        correct: 2
      }
    },
    {
      id: 6,
      title: "Le XXe siècle",
      subtitle: "Guerres, radio et télévision",
      color: "#2ECC71",
      icon: "📻",
      timeline: [
        { year: "1914", event: "Presse française : 600 titres, 244 ex/1000 hab" },
        { year: "1915", event: "Création du Canard enchaîné" },
        { year: "1944", event: "Libération : renaissance de la presse" },
        { year: "1973", event: "Crise pétrolière, montée de la TV" }
      ],
      lessons: [
        {
          title: "La Grande Guerre et le bourrage de crâne",
          points: [
            "1914 : la France est le pays le plus lecteur au monde",
            "Albert Londres dénonce la propagande",
            "Le Canard enchaîné naît pour contrer les mensonges"
          ],
          keyFact: "Le Canard enchaîné existe depuis 1915 !"
        },
        {
          title: "L'Occupation et la Résistance",
          points: [
            "Journaux contrôlés par les Allemands",
            "Presse clandestine : Combat, Libération, Défense de la France",
            "Camus écrit dans Combat"
          ],
          keyFact: "Les journalistes résistants risquaient leur vie !"
        },
        {
          title: "La concurrence TV et radio",
          points: [
            "1968 : 62% des familles ont la TV",
            "1973 : choc pétrolier, crise économique",
            "Les magazines explosent (Express, Nouvel Obs, Le Point)"
          ],
          keyFact: "La TV a failli tuer la presse écrite !"
        }
      ],
      quiz: {
        question: "Pourquoi Le Canard enchaîné a-t-il été créé en 1915 ?",
        options: ["Pour faire rire", "Contre la censure et la propagande", "Pour parler de politique", "Pour les canards"],
        correct: 1
      }
    },
    {
      id: 7,
      title: "Le XXIe siècle",
      subtitle: "Internet et crise de la presse",
      color: "#3498DB",
      icon: "💻",
      timeline: [
        { year: "2000s", event: "Essor d'Internet et de la presse gratuite" },
        { year: "2007", event: "Création de Mediapart, Rue89" },
        { year: "2009", event: "France 43e au classement RSF" },
        { year: "2012", event: "Crise économique des médias" }
      ],
      lessons: [
        {
          title: "La révolution numérique",
          points: [
            "Google Actualités, Yahoo : infos gratuites",
            "Les quotidiens papier perdent des lecteurs",
            "La presse gratuite (20 Minutes, Metro) explose"
          ],
          keyFact: "Pourquoi payer quand c'est gratuit en ligne ?"
        },
        {
          title: "Les pure players",
          points: [
            "Mediapart, Rue89, Bakchich : 100% web",
            "Créés par des journalistes expérimentés",
            "Nouveau modèle économique"
          ],
          keyFact: "Mediapart a révélé l'affaire Cahuzac !"
        },
        {
          title: "Les défis actuels",
          points: [
            "Censure et intimidations dans le monde",
            "Précarisation du métier de journaliste",
            "La désinformation comme nouveau défi"
          ],
          keyFact: "Le combat pour la liberté de la presse continue !"
        }
      ],
      quiz: {
        question: "Quel journal en ligne a révélé l'affaire Cahuzac ?",
        options: ["Rue89", "Mediapart", "Le Monde", "Bakchich"],
        correct: 1
      }
    }
  ]
};

// Fonction pour mélanger un tableau (Fisher-Yates shuffle)
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function PressHistoryPage() {
  const navigate = useNavigate();
  const [currentEpoch, setCurrentEpoch] = useState(0);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [currentPoint, setCurrentPoint] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [completedEpochs, setCompletedEpochs] = useState([]);
  const [showTimeline, setShowTimeline] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [professorQuote, setProfessorQuote] = useState('');
  const [showBadge, setShowBadge] = useState(false);
  const [shuffledQuiz, setShuffledQuiz] = useState({ options: [], correctIndex: 0 });

  const epoch = COURSE_DATA.epochs[currentEpoch];
  const lesson = epoch?.lessons[currentLesson];
  const totalLessons = epoch?.lessons.length || 0;
  const totalPoints = lesson?.points.length || 0;

  // Charger la progression sauvegardée
  useEffect(() => {
    const saved = localStorage.getItem('pressHistoryProgress');
    if (saved) {
      const progress = JSON.parse(saved);
      setCurrentEpoch(progress.epoch || 0);
      setCurrentLesson(progress.lesson || 0);
      setCurrentPoint(progress.point || 0);
      setCompletedEpochs(progress.completed || []);
    }
    setProfessorQuote(getRandomQuote(COURSE_DATA.professor));
  }, []);

  // Sauvegarder la progression
  useEffect(() => {
    localStorage.setItem('pressHistoryProgress', JSON.stringify({
      epoch: currentEpoch,
      lesson: currentLesson,
      point: currentPoint,
      completed: completedEpochs
    }));
  }, [currentEpoch, currentLesson, currentPoint, completedEpochs]);

  // Auto-play
  useEffect(() => {
    if (autoPlay) {
      const timer = setTimeout(() => {
        handleNext();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, currentPoint, currentLesson, currentEpoch]);

  const handleNext = () => {
    if (currentPoint < totalPoints - 1) {
      setCurrentPoint(currentPoint + 1);
    } else if (currentLesson < totalLessons - 1) {
      setCurrentLesson(currentLesson + 1);
      setCurrentPoint(0);
    } else {
      // Mélanger les réponses du quiz avant de l'afficher
      const quiz = epoch.quiz;
      const optionsWithIndex = quiz.options.map((opt, idx) => ({ text: opt, originalIndex: idx }));
      const shuffled = shuffleArray(optionsWithIndex);
      const newCorrectIndex = shuffled.findIndex(opt => opt.originalIndex === quiz.correct);
      setShuffledQuiz({
        options: shuffled.map(opt => opt.text),
        correctIndex: newCorrectIndex
      });
      setShowQuiz(true);
      setAutoPlay(false);
    }
  };

  const handlePrev = () => {
    if (currentPoint > 0) {
      setCurrentPoint(currentPoint - 1);
    } else if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
      setCurrentPoint(epoch.lessons[currentLesson - 1].points.length - 1);
    } else if (currentEpoch > 0) {
      const prevEpoch = COURSE_DATA.epochs[currentEpoch - 1];
      setCurrentEpoch(currentEpoch - 1);
      setCurrentLesson(prevEpoch.lessons.length - 1);
      setCurrentPoint(prevEpoch.lessons[prevEpoch.lessons.length - 1].points.length - 1);
      setShowQuiz(false);
    }
  };

  const handleQuizAnswer = (index) => {
    setQuizAnswer(index);
    if (index === shuffledQuiz.correctIndex) {
      if (!completedEpochs.includes(currentEpoch)) {
        setCompletedEpochs([...completedEpochs, currentEpoch]);
      }
      setTimeout(() => {
        if (currentEpoch < COURSE_DATA.epochs.length - 1) {
          setCurrentEpoch(currentEpoch + 1);
          setCurrentLesson(0);
          setCurrentPoint(0);
          setShowQuiz(false);
          setQuizAnswer(null);
          setProfessorQuote(getRandomQuote(COURSE_DATA.professor));
        }
      }, 2000);
    }
  };

  const goToEpoch = (index) => {
    setCurrentEpoch(index);
    setCurrentLesson(0);
    setCurrentPoint(0);
    setShowQuiz(false);
    setQuizAnswer(null);
    setShowTimeline(false);
  };

  const resetProgress = () => {
    setCurrentEpoch(0);
    setCurrentLesson(0);
    setCurrentPoint(0);
    setCompletedEpochs([]);
    setShowQuiz(false);
    setQuizAnswer(null);
    localStorage.removeItem('pressHistoryProgress');
  };

  return (
    <div className="min-h-screen" style={{ background: '#2C3E50' }}>
      {/* Header */}
      <header className="border-b-4 border-black" style={{ background: '#1B4965' }}>
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-white hover:text-yellow-400"
            data-testid="back-button"
          >
            <FaArrowLeft />
            <span className="comic-text">Retour</span>
          </button>
          <h1 className="comic-title text-xl text-white flex items-center">
            <FaBook className="mr-2" /> {COURSE_DATA.title}
          </h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowBadge(true)}
              className="comic-button bg-yellow-400 text-black text-sm px-3 py-1"
              title="Voir mon badge"
              data-testid="share-badge-btn"
            >
              <FaShare />
            </button>
            <span className="text-white/70 text-sm">
              {completedEpochs.length}/{COURSE_DATA.epochs.length}
            </span>
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className="comic-button bg-yellow-400 text-black text-sm px-3 py-1"
              data-testid="timeline-btn"
            >
              <FaClock />
            </button>
          </div>
        </div>
      </header>

      {/* Badge modal */}
      <AnimatePresence>
        {showBadge && (
          <ShareableBadge
            courseName={COURSE_DATA.title}
            completedEpochs={completedEpochs.length}
            totalEpochs={COURSE_DATA.epochs.length}
            onClose={() => setShowBadge(false)}
          />
        )}
      </AnimatePresence>

      {/* Progress bar */}
      <div className="h-2 bg-gray-700">
        <motion.div 
          className="h-full"
          style={{ background: epoch?.color }}
          initial={{ width: 0 }}
          animate={{ 
            width: `${((currentEpoch * 100 / COURSE_DATA.epochs.length) + 
              (currentLesson * 100 / COURSE_DATA.epochs.length / totalLessons) +
              (currentPoint * 100 / COURSE_DATA.epochs.length / totalLessons / Math.max(1, totalPoints)))}%` 
          }}
        />
      </div>

      {/* Timeline sidebar */}
      <AnimatePresence>
        {showTimeline && (
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="fixed left-0 top-20 bottom-0 w-72 bg-gray-800 border-r-4 border-black z-50 overflow-y-auto"
          >
            <div className="p-4">
              <h3 className="comic-title text-white mb-4">Frise Chronologique</h3>
              {COURSE_DATA.epochs.map((ep, idx) => (
                <button
                  key={idx}
                  onClick={() => goToEpoch(idx)}
                  className={`w-full text-left p-3 mb-2 rounded-lg border-2 transition-all ${
                    idx === currentEpoch 
                      ? 'border-yellow-400 bg-yellow-400/20' 
                      : 'border-gray-600 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">{ep.icon}</span>
                    <div className="flex-1">
                      <p className="comic-text text-white text-sm">{ep.title}</p>
                      <p className="text-xs text-gray-400">{ep.subtitle}</p>
                    </div>
                    {completedEpochs.includes(idx) && (
                      <FaCheckCircle className="text-green-400" />
                    )}
                  </div>
                </button>
              ))}
              <button
                onClick={resetProgress}
                className="w-full mt-4 comic-button bg-red-500 text-white text-sm"
              >
                <FaRedo className="mr-1" /> Recommencer
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Professor */}
          <div className="lg:col-span-1">
            <motion.div 
              className="comic-panel text-center sticky top-4"
              style={{ background: '#F8F4E3' }}
            >
              <img 
                src={COURSE_DATA.professor.image}
                alt={COURSE_DATA.professor.name}
                className="w-32 h-32 mx-auto object-contain mb-3"
              />
              <h3 className="comic-text text-lg" style={{ color: '#1B4965' }}>
                {COURSE_DATA.professor.name}
              </h3>
              <p className="text-xs text-gray-500 mb-2">Votre professeur</p>
              <div className="speech-bubble text-sm">
                <FaQuoteLeft className="text-gray-300 mb-1" />
                <p className="italic">"{professorQuote}"</p>
              </div>
            </motion.div>

            {/* Mini timeline for current epoch */}
            <motion.div 
              className="comic-panel mt-4 hidden lg:block"
              style={{ background: epoch?.color + '20' }}
            >
              <h4 className="comic-text text-sm mb-2" style={{ color: epoch?.color }}>
                Dates clés
              </h4>
              <div className="space-y-2">
                {epoch?.timeline.map((item, idx) => (
                  <div key={idx} className="flex items-start text-xs">
                    <span className="font-bold mr-2" style={{ color: epoch?.color }}>
                      {item.year}
                    </span>
                    <span className="text-gray-600">{item.event}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Blackboard */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {!showQuiz ? (
                <motion.div
                  key={`${currentEpoch}-${currentLesson}-${currentPoint}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="relative"
                >
                  {/* Blackboard frame */}
                  <div 
                    className="rounded-lg p-1"
                    style={{ background: '#5D4037' }}
                  >
                    {/* Chalk tray */}
                    <div className="h-3 bg-amber-200 rounded-t-sm mb-1" />
                    
                    {/* Blackboard */}
                    <div 
                      className="rounded p-6 min-h-[400px]"
                      style={{ 
                        background: 'linear-gradient(135deg, #1a472a 0%, #0d3320 50%, #1a472a 100%)',
                        boxShadow: 'inset 0 0 50px rgba(0,0,0,0.5)'
                      }}
                    >
                      {/* Epoch badge */}
                      <div className="flex justify-between items-start mb-4">
                        <div 
                          className="inline-flex items-center px-4 py-2 rounded-full"
                          style={{ background: epoch?.color }}
                        >
                          <span className="text-2xl mr-2">{epoch?.icon}</span>
                          <span className="comic-text text-white">{epoch?.title}</span>
                        </div>
                        <div className="text-white/50 text-sm">
                          Leçon {currentLesson + 1}/{totalLessons}
                        </div>
                      </div>

                      {/* Lesson title - chalk effect */}
                      <motion.h2 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-2xl md:text-3xl mb-6 text-center"
                        style={{ 
                          fontFamily: 'Bangers, cursive',
                          color: '#F5DEB3',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                        }}
                      >
                        {lesson?.title}
                      </motion.h2>

                      {/* Points displayed one by one */}
                      <div className="space-y-4">
                        {lesson?.points.slice(0, currentPoint + 1).map((point, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx === currentPoint ? 0.3 : 0 }}
                            className="flex items-start"
                          >
                            <span 
                              className="text-2xl mr-3 mt-1"
                              style={{ color: '#FFD700' }}
                            >
                              ▸
                            </span>
                            <p 
                              className="text-lg md:text-xl"
                              style={{ 
                                fontFamily: "'Patrick Hand', cursive",
                                color: 'white',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                              }}
                            >
                              {point}
                            </p>
                          </motion.div>
                        ))}
                      </div>

                      {/* Key fact */}
                      {currentPoint === totalPoints - 1 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 }}
                          className="mt-8 p-4 rounded-lg"
                          style={{ background: 'rgba(255,215,0,0.2)', border: '2px dashed #FFD700' }}
                        >
                          <div className="flex items-center">
                            <FaLightbulb className="text-3xl mr-3" style={{ color: '#FFD700' }} />
                            <p 
                              className="text-lg"
                              style={{ 
                                fontFamily: "'Patrick Hand', cursive",
                                color: '#FFD700'
                              }}
                            >
                              {lesson?.keyFact}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={handlePrev}
                      disabled={currentEpoch === 0 && currentLesson === 0 && currentPoint === 0}
                      className="comic-button bg-gray-600 text-white disabled:opacity-50"
                    >
                      <FaArrowLeft className="mr-2" /> Précédent
                    </button>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setAutoPlay(!autoPlay)}
                        className={`comic-button ${autoPlay ? 'bg-red-500' : 'bg-green-500'} text-white`}
                      >
                        {autoPlay ? <FaPause /> : <FaPlay />}
                      </button>
                      <span className="text-white/70 text-sm">
                        Point {currentPoint + 1}/{totalPoints}
                      </span>
                    </div>

                    <button
                      onClick={handleNext}
                      className="comic-button text-white"
                      style={{ background: epoch?.color }}
                    >
                      Suivant <FaArrowRight className="ml-2" />
                    </button>
                  </div>
                </motion.div>
              ) : (
                /* Quiz */
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="comic-panel"
                  style={{ background: '#F8F4E3' }}
                >
                  <div className="text-center mb-6">
                    <span className="text-4xl">{epoch?.icon}</span>
                    <h2 className="comic-title text-2xl mt-2" style={{ color: epoch?.color }}>
                      Quiz - {epoch?.title}
                    </h2>
                  </div>

                  <div className="max-w-xl mx-auto">
                    <p className="comic-text text-xl text-center mb-6">
                      {epoch?.quiz.question}
                    </p>

                    <div className="space-y-3">
                      {shuffledQuiz.options.map((option, idx) => (
                        <motion.button
                          key={idx}
                          whileHover={{ scale: quizAnswer === null ? 1.02 : 1 }}
                          whileTap={{ scale: quizAnswer === null ? 0.98 : 1 }}
                          onClick={() => quizAnswer === null && handleQuizAnswer(idx)}
                          disabled={quizAnswer !== null}
                          className={`w-full p-4 rounded-lg border-3 text-left transition-all ${
                            quizAnswer === null 
                              ? 'border-gray-300 hover:border-gray-500 bg-white' 
                              : idx === shuffledQuiz.correctIndex
                                ? 'border-green-500 bg-green-100'
                                : idx === quizAnswer
                                  ? 'border-red-500 bg-red-100'
                                  : 'border-gray-200 bg-gray-50 opacity-50'
                          }`}
                        >
                          <span className="comic-text">
                            {String.fromCharCode(65 + idx)}. {option}
                          </span>
                          {quizAnswer !== null && idx === shuffledQuiz.correctIndex && (
                            <FaCheckCircle className="inline ml-2 text-green-500" />
                          )}
                        </motion.button>
                      ))}
                    </div>

                    {quizAnswer !== null && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-6 p-4 rounded-lg text-center ${
                          quizAnswer === shuffledQuiz.correctIndex 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {quizAnswer === shuffledQuiz.correctIndex ? (
                          <>
                            <p className="comic-text text-xl">Bravo ! 🎉</p>
                            <p className="text-sm mt-1">Passage à l'époque suivante...</p>
                          </>
                        ) : (
                          <>
                            <p className="comic-text text-xl">Pas tout à fait... 🤔</p>
                            <button
                              onClick={() => setQuizAnswer(null)}
                              className="comic-button bg-red-500 text-white mt-2"
                            >
                              Réessayer
                            </button>
                          </>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Epoch completion */}
            {currentEpoch === COURSE_DATA.epochs.length - 1 && showQuiz && quizAnswer === shuffledQuiz.correctIndex && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 comic-panel text-center"
                style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' }}
              >
                <h2 className="comic-title text-3xl text-white mb-2">
                  Félicitations ! 🏆
                </h2>
                <p className="text-white text-lg mb-4">
                  Tu as terminé le cours "Histoire de la Presse" !
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setShowBadge(true)}
                    className="comic-button bg-white text-gray-800"
                  >
                    <FaShare className="mr-2" /> Partager mon badge
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="comic-button bg-green-500 text-white"
                  >
                    <FaHome className="mr-2" /> Retour au Dashboard
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PressHistoryPage;
